/**
 * Controller I/O Composable
 *
 * Manages controller I/O allocation state and API operations.
 * Handles terminal allocation, wiring diagram data, and programming status.
 */

import { ref, computed } from 'vue';
import { api } from '@/services/api';
import { formatBrickClass } from '@/composables/brick-schema/operations/useBrickValidation.js';

/**
 * I/O Type definitions with colors and labels
 */
export const IO_TYPES = [
  { value: 'ai', label: 'AI', color: 'blue', fullName: 'Analog Input' },
  { value: 'ao', label: 'AO', color: 'green', fullName: 'Analog Output' },
  { value: 'di', label: 'DI', color: 'orange', fullName: 'Digital Input' },
  { value: 'do', label: 'DO', color: 'red', fullName: 'Digital Output' },
  { value: 'ui', label: 'UI', color: 'purple', fullName: 'Universal Input' },
  { value: 'uo', label: 'UO', color: 'teal', fullName: 'Universal Output' },
];

/**
 * Get color for terminal type
 * @param {string} type - Terminal type (ai, ao, di, do, ui, uo)
 * @returns {string} Quasar color name
 */
export function getTerminalColor(type) {
  const ioType = IO_TYPES.find((t) => t.value === type);
  return ioType?.color || 'grey';
}

/**
 * Get label for terminal type
 * @param {string} type - Terminal type
 * @returns {string} Display label
 */
export function getTypeLabel(type) {
  const ioType = IO_TYPES.find((t) => t.value === type);
  return ioType?.label || type?.toUpperCase();
}

// Re-export formatBrickClass from the canonical location
export { formatBrickClass };

/**
 * Create a controller I/O management composable
 * @param {number} siteId - Site ID
 * @param {number} equipmentId - Controller equipment ID
 * @returns {Object} Controller I/O state and operations
 */
export function useControllerIO(siteId, equipmentId) {
  // State
  const controller = ref(null);
  const allocations = ref([]);
  const summary = ref(null);
  const equipmentServed = ref([]);
  const availablePoints = ref([]);
  const programmingStatus = ref(null);
  const wiringDiagram = ref(null);
  const pointsListData = ref(null);
  const loading = ref(false);
  const assigning = ref(false);
  const exporting = ref(false);
  const error = ref(null);

  /**
   * Load I/O allocation data from API
   */
  const loadIOAllocation = async () => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.get(`/sites/${siteId}/equipment/${equipmentId}/io-allocation`);

      controller.value = response.controller;
      allocations.value = response.allocations || [];
      summary.value = response.summary;
      equipmentServed.value = response.equipment_served || [];

      return response;
    } catch (err) {
      error.value = err.message || 'Failed to load I/O allocation';
      console.error('[Controller I/O] Load error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Allocate a point to a terminal
   * @param {string} terminal - Terminal name (e.g., "AI-1")
   * @param {number} pointId - Point ID to allocate
   * @param {string} wireColor - Wire color (optional)
   * @param {string} wireNumber - Wire number/label (optional)
   */
  const allocatePoint = async (terminal, pointId, wireColor = null, wireNumber = null) => {
    assigning.value = true;
    error.value = null;

    try {
      const response = await api.post(`/sites/${siteId}/equipment/${equipmentId}/io-allocation`, {
        terminal,
        point_id: pointId,
        wire_color: wireColor,
        wire_number: wireNumber,
      });

      // Reload allocations to get fresh data
      await loadIOAllocation();

      return response;
    } catch (err) {
      error.value = err.message || 'Failed to allocate point';
      console.error('[Controller I/O] Allocate error:', err);
      throw err;
    } finally {
      assigning.value = false;
    }
  };

  /**
   * Update an existing allocation
   * @param {string} terminal - Terminal name
   * @param {Object} updateData - Data to update (point_id, wire_color, wire_number)
   */
  const updateAllocation = async (terminal, updateData) => {
    assigning.value = true;
    error.value = null;

    try {
      const response = await api.put(
        `/sites/${siteId}/equipment/${equipmentId}/io-allocation/${terminal}`,
        updateData,
      );

      // Reload allocations to get fresh data
      await loadIOAllocation();

      return response;
    } catch (err) {
      error.value = err.message || 'Failed to update allocation';
      console.error('[Controller I/O] Update error:', err);
      throw err;
    } finally {
      assigning.value = false;
    }
  };

  /**
   * Remove allocation from a terminal
   * @param {string} terminal - Terminal name
   */
  const removeAllocation = async (terminal) => {
    error.value = null;

    try {
      await api.delete(`/sites/${siteId}/equipment/${equipmentId}/io-allocation/${terminal}`);

      // Reload allocations to get fresh data
      await loadIOAllocation();
    } catch (err) {
      error.value = err.message || 'Failed to remove allocation';
      console.error('[Controller I/O] Remove error:', err);
      throw err;
    }
  };

  /**
   * Load available unallocated points
   */
  const loadAvailablePoints = async () => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.get(
        `/sites/${siteId}/equipment/${equipmentId}/io-allocation/available-points`,
      );

      availablePoints.value = response || [];
      return response;
    } catch (err) {
      error.value = err.message || 'Failed to load available points';
      console.error('[Controller I/O] Load available points error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Load programming status for equipment served by controller
   */
  const loadProgrammingStatus = async () => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.get(
        `/sites/${siteId}/equipment/${equipmentId}/programming-status`,
      );

      programmingStatus.value = response;
      return response;
    } catch (err) {
      error.value = err.message || 'Failed to load programming status';
      console.error('[Controller I/O] Load programming status error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Load wiring diagram data
   */
  const loadWiringDiagram = async () => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.get(
        `/sites/${siteId}/equipment/${equipmentId}/wiring-diagram`,
      );

      wiringDiagram.value = response;
      return response;
    } catch (err) {
      error.value = err.message || 'Failed to load wiring diagram';
      console.error('[Controller I/O] Load wiring diagram error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Export wiring diagram to PDF
   */
  const exportWiringPdf = async () => {
    exporting.value = true;
    error.value = null;

    try {
      // Call browser print (frontend implementation)
      window.print();
    } catch (err) {
      error.value = err.message || 'Failed to export wiring diagram';
      console.error('[Controller I/O] Export error:', err);
      throw err;
    } finally {
      exporting.value = false;
    }
  };

  /**
   * Load points list data (for PDF generation)
   */
  const loadPointsList = async () => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.get(
        `/sites/${siteId}/equipment/${equipmentId}/points-list`,
      );

      pointsListData.value = response;
      return response;
    } catch (err) {
      error.value = err.message || 'Failed to load points list';
      console.error('[Controller I/O] Load points list error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Swap allocations between two terminals
   * @param {string} terminalA - First terminal name (e.g., "UI-1")
   * @param {string} terminalB - Second terminal name (e.g., "UI-2")
   */
  const swapTerminals = async (terminalA, terminalB) => {
    assigning.value = true;
    error.value = null;

    try {
      const response = await api.post(
        `/sites/${siteId}/equipment/${equipmentId}/points-list/swap`,
        {
          terminal_a: terminalA,
          terminal_b: terminalB,
        },
      );

      // Reload points list to show updated allocations
      await loadPointsList();

      return response;
    } catch (err) {
      error.value = err.message || 'Failed to swap allocations';
      console.error('[Controller I/O] Swap error:', err);
      throw err;
    } finally {
      assigning.value = false;
    }
  };

  /**
   * Export points list to PDF
   * Note: Uses direct fetch instead of api.get() because api.get() doesn't support blob responses
   */
  const exportPointsListPdf = async () => {
    exporting.value = true;
    error.value = null;

    try {
      // Get auth token from session storage
      const token = sessionStorage.getItem('token');
      const headers = {
        'Accept': 'application/pdf',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Use direct fetch for blob response - api.get() doesn't support responseType: 'blob'
      const response = await fetch(
        `/api/sites/${siteId}/equipment/${equipmentId}/points-list/pdf`,
        {
          method: 'GET',
          headers,
          credentials: 'include',
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `points-list-${equipmentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return blob;
    } catch (err) {
      error.value = err.message || 'Failed to export points list PDF';
      console.error('[Controller I/O] Export PDF error:', err);
      throw err;
    } finally {
      exporting.value = false;
    }
  };

  // Computed properties
  const allocatedCount = computed(() => {
    return allocations.value.filter((a) => a.point_id).length;
  });

  const totalIOCount = computed(() => {
    return summary.value?.total_io || allocations.value.length;
  });

  const allocationPercentage = computed(() => {
    if (totalIOCount.value === 0) {return 0;}
    return Math.round((allocatedCount.value / totalIOCount.value) * 100);
  });

  return {
    // State
    controller,
    allocations,
    summary,
    equipmentServed,
    availablePoints,
    programmingStatus,
    wiringDiagram,
    pointsListData,
    loading,
    assigning,
    exporting,
    error,

    // Computed
    allocatedCount,
    totalIOCount,
    allocationPercentage,

    // Actions
    loadIOAllocation,
    allocatePoint,
    updateAllocation,
    removeAllocation,
    loadAvailablePoints,
    loadProgrammingStatus,
    loadWiringDiagram,
    exportWiringPdf,
    loadPointsList,
    exportPointsListPdf,
    swapTerminals,
  };
}
